/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  Component,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  EventEmitter,
  TemplateRef,
  Inject,
  ChangeDetectorRef,
  inject,
  PLATFORM_ID,
  computed,
  effect,
  input,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { SelectButtonChangeEvent, SelectButtonModule, SelectButtonOptionClickEvent } from 'primeng/selectbutton';
import { filter, firstValueFrom, Subject, take } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { MovieLoadingComponent, MovieDetailSkeletonComponent } from '@usheru-hff/ui/movie-detail-page';
import { MovieDetailFeatureAtFestivalsModule } from '@usheru-hff/movie-detail/feature-at-festivals';
import { MovieDetailFeatureEnquiriesModule } from '@usheru-hff/movie-detail/feature-enquiries';
import { MovieDetailFeatureShowtimesModule } from '@usheru-hff/movie-detail/feature-showtimes';
import { MovieDetailFeatureStreamingModule } from '@usheru-hff/movie-detail/feature-streaming';
import {
  ShowtimeStreamingSubscribeComponent,
  FormSubscriptionComponent
} from '@usheru-hff/movie-detail/feature-subscribe';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { Country, Movie, ShowtimesTab, TabType, getMovieGenres, getMovieTags } from '@usheru-hff/shared/domain';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { getDateFromDDMMYYYY, LogService, ScopedLogger, SharedUtilsModule } from '@usheru-hff/shared/utils';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TitleLocalizePipe } from '@usheru-hff/ui/pipes';

/**
 * Component to show in what places where a movie is available.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "where-to-watch".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - this could be simplificated a lot. Improbe the set of pageType, remove ngOnInit and use ngOnChange.
 * //TODO barbeitoalex propostal - use environment values in their own method and subscribe to environment changes
 * //TODO barbeitoalex propostal - move component folder to the root of components
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-showtime-streaming',
  templateUrl: './showtime-streaming.component.html',
  styleUrls: ['./showtime-streaming.component.scss'],
  standalone: true,
  animations: [
    trigger('showtimeLoadingSwap', [
      transition(':enter', [style({ opacity: 0 }), animate('220ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('220ms ease-in', style({ opacity: 0 }))])
    ])
  ],
  imports: [
    CommonModule,
    SelectButtonModule,
    SharedUtilsModule,
    FormsModule,
    MovieDetailFeatureShowtimesModule,
    ShowtimeStreamingSubscribeComponent,
    MovieDetailFeatureEnquiriesModule,
    MovieDetailFeatureStreamingModule,
    MovieDetailFeatureAtFestivalsModule,
    TitleLocalizePipe,
    MovieDetailSkeletonComponent
  ]
})
export class ShowtimeStreamingComponent implements OnChanges, MovieLoadingComponent {
  protected readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger: ScopedLogger = inject(LogService).scoped('showtime-streaming');
  private readonly applicationRef = inject(ApplicationRef);
  /**
   * @docsNotRequired
   */
  // @Input()
  readonly movie = input<Movie | undefined>(undefined);
  readonly isLoadingData = input(false);
  private readonly isHydrating = signal(this.isBrowser);
  protected readonly animationsDisabled = computed(() => this.isHydrating());
  private readonly isResolvingActiveType = signal(false);
  readonly isLoadingShowtimes = computed(
    () => !this.isHydrating() && (this.isLoadingData() || this.isResolvingActiveType())
  );

  tab = input<string>();

  /**
   * Type of availavility of the movie to show.
   */
  // @Input()
  activeType: ShowtimesTab;

  availableTypes: ShowtimesTab[] = ['in-cinemas', 'watch-at-home', 'at-festivals', 'enquiries'];

  @Input() customSubscribeTitleTr: string;

  @Input() customSubscribeDescTr: string;

  @Input() openFirstPanel = false;

  readonly onlySubscription = input<boolean>(false);

  /**
   * Used to know the `activeType` if `onlySubscription` is true.
   */
  readonly onlySubscriptionType = input<ShowtimesTab>(null);

  /**
   * @deprecard Don't found any use
   */
  params: Params;

  /**
   * If the there is only one {@link pageTypesOptions} shows the tab title.
   */
  @Input() showStreamingTitleAlways: boolean;

  @Input() inlineMap = false;

  /**
   * Used to prevent launch the tracking on multiple clicks in the {@link onChangeType} event.
   */
  beforeType: ShowtimesTab;

  /**
   * Configuration for {@link ShowtimeStreamingSubscribeComponent}.
   *
   * //TODO barbeitoalex propostal - this value is necesary?
   */
  subscribeType: ShowtimesTab;

  /**
   * List of tabs to show.
   */
  pageTypes: ShowtimesTab[] = [];

  /**
   * Map version of {@link pageTypes}.
   */
  pageTypesOptions: SelectItem[] = [];

  /**
   * @deprecard Don't found any use
   */
  country: Country;

  /**
   * Used to set {@link pageTypes}. Activates if {@link areShowtimesAvailable}
   * and {@link disableShowtimes} is disabled.
   *
   * //TODO barbeitoalex propostal - Could be change to a get method and skip description.
   */
  showInCinemas = false;

  /**
   *  Used to set {@link pageTypes}. Activates if {@link movie}.locStreamingAvailable
   * or {@link isUplayerAvailable} and {@link disableStreaming} is disabled.
   *
   * //TODO barbeitoalex propostal - Could be change to a get method and skip description.
   */
  showWatchAtHome = false;

  /**
   * Used to set {@link pageTypes}. Activates if {@link movie}.movieFestivals.
   *
   * //TODO barbeitoalex propostal - Could be change to a get method and skip description.
   */
  showAtFestivals = false;

  /**
   * Used to set {@link pageTypes}. Activates if {@link forceDisableSubscription} is false
   * and {@link Channel} or {@link Movie} property `emailSubscriptionEnabled` is present.
   */
  showDemandItForm = false;

  /**
   * Used to set {@link pageTypes}. Activates from {@link Environment} enquiries.
   */
  showEnquiries = false;

  @Input() hideTabs?: ShowtimesTab[];

  // Templates refs
  @Input() inCinemasAddon?: TemplateRef<unknown>;

  /**
   * @docsNotRequired
   */
  private readonly disableShowtimes;

  /**
   * @docsNotRequired
   */
  private readonly disableStreaming;

  /**
   * Disables {@link showDemandItForm}.
   */
  @Input()
  forceDisableSubscription = false;

  @Input()
  enableRouterNavigation = true;

  @Input()
  showUsheruLogo = false;

  @Input()
  disableUplayer = false;

  @Input()
  widgetEnableTabs: TabType[] = [];

  /**
   * //TODO docs
   */
  @Output()
  servicesAvailables: EventEmitter<(TabType | 'subscribe')[]> = new EventEmitter();

  get paramType(): TabType {
    return this.activeType;
  }

  /**
   * Setted in {@link setUsePlaceholderData}.
   */
  usePlaceholderPerformancesGroup = false;

  /**
   * Setted in {@link setUsePlaceholderData}.
   */
  usePlaceholderStreamingLinks = false;

  streamingPlatformsLoadedTracked: boolean;
  showtimesLoadedTracked: boolean;
  noOptionsMessage: string = '';
  noOptionsMessageKey: string = '';
  destroy$ = new Subject<void>();

  constructor(
    private channelService: ChannelService,
    private router: Router,
    private location: LocationService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private cd: ChangeDetectorRef,
    @Inject(ENVIRONMENT) private env: Environment,
    private showtimeStreamingService: ShowtimeStreamingService
  ) {
    this.disableShowtimes = this.channelService.channel.settings.disableShowtimes;
    this.disableStreaming = this.channelService.channel.settings.disableStreaming;
    this.forceDisableSubscription = this.env?.hideSubscriptionComponent;
    this.country = this.location.selectedCountry;
    effect(
      () => {
        const movie = this.movie();
        if (!this.onlySubscription()) {
          this.evaluateShowtimesStreamingAvailability(movie);
          this.setUpNoOptionsMessage();
          this.filterVisibleTabs();
          this.evaluateActiveType();
        }
        this.evaluateComsumerDemandItSubscription();
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        if (this.onlySubscription() && this.onlySubscriptionType()) {
          this.setActiveType(this.onlySubscriptionType());
        }
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        if (this.tab()) {
          this.evaluateActiveType();
        }
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * @docsNotRequired
   */
  ngOnInit() {
    this.setUsePlaceholderData();
    if (this.env.showStreamingTitleAlways && this.showStreamingTitleAlways !== false)
      this.showStreamingTitleAlways = true;
    if (this.isBrowser) {
      this.applicationRef.isStable.pipe(filter(Boolean), take(1)).subscribe(() => {
        this.isHydrating.set(false);
        this.cd.detectChanges();
      });
    }
  }

  // function that will determiny what value to set in the activeType variable
  evaluateActiveType() {
    const movie = this.movie();
    if (!movie) {
      this.isResolvingActiveType.set(false);
      this.setActiveType(null);
      return;
    }

    this.isResolvingActiveType.set(true);
    firstValueFrom(this.showtimeStreamingService.determineShowtimeStreamingActiveType(movie, this.hideTabs))
      .then(activeType => {
        if (activeType) {
          this.setActiveType(activeType);
          this.cd.detectChanges();
        } else {
          this.setActiveType(this.showInCinemas ? 'in-cinemas' : this.showWatchAtHome ? 'watch-at-home' : null);
        }
      })
      .finally(() => {
        this.isResolvingActiveType.set(false);
      });
  }

  evaluateShowtimesStreamingAvailability(movie: Movie | null) {
    if (!movie) {
      this.showInCinemas = false;
      this.showWatchAtHome = false;
      this.showAtFestivals = false;
      this.showEnquiries = !!this.showtimeStreamingService.determineEnquiriesAvailability();
      return;
    }

    // verify if in cinemas and streaming are not disabled by hideBrandedSections
    if (this.hideTabs?.includes('in-cinemas')) {
      this.showInCinemas = false;
    } else {
      this.showInCinemas = this.showtimeStreamingService.determineInCinemasAvailability(movie);
    }
    if (this.hideTabs?.includes('watch-at-home')) {
      this.showWatchAtHome = false;
    } else {
      this.showWatchAtHome = this.showtimeStreamingService.determineStreamingAvailability(movie);
    }
    this.showEnquiries = !!this.showtimeStreamingService.determineEnquiriesAvailability();
    this.showAtFestivals = this.showtimeStreamingService.determineFestivalsAvailability(movie);
  }

  evaluateComsumerDemandItSubscription() {
    if (this.forceDisableSubscription) {
      this.showDemandItForm = false;
      return;
    }
    this.displayDemandItForm();
  }

  filterVisibleTabs() {
    // filtering what witgets can be shown

    // method that will populate this.pageTypes with the available tabs
    this.enableTabs();

    // pageTypesOptions is the map version of pageTypes
    this.pageTypesOptions = this.pageTypes.map(type => ({
      label: type,
      value: type
    }));
  }

  /**
   * * Set the {@link pageType} after {@link movie} changes.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['movie']?.firstChange) return;
    // if (changes['activeType']?.currentValue !== changes['activeType']?.previousValue) {
    //   this.onChangeType(this.activeType);
    // }

    if (
      this.moviesAreDifferent(changes.movie?.currentValue as Movie | null, changes.movie?.previousValue as Movie | null)
    ) {
      const movie = changes['movie']?.currentValue;
      // const type = changes['activeType']?.currentValue;
      this.showtimesLoadedTracked = false;
      this.streamingPlatformsLoadedTracked = false;

      this.evaluateShowtimesStreamingAvailability(movie);
      this.filterVisibleTabs();

      const services = this.pageTypesOptions.map(item => item.value);

      if (this.showDemandItForm) services.push('subscribe');
      this.servicesAvailables.next(services);

      // if during movie detail routing active type is undefiend use the first pageType Option available
      // if ((!this.activeType || this.activeTypeDoesNotExists()) && this.pageTypes?.length > 0) {
      //   this.setActiveType(this.pageTypes[0]);
      // }
    }
  }

  moviesAreDifferent(previousMovie: Movie | null, newMovie: Movie | null): boolean {
    if (
      previousMovie?.friendlyUrl != newMovie?.friendlyUrl ||
      previousMovie?.locStreamingAvailable != newMovie?.locStreamingAvailable ||
      previousMovie?.locCinemaEnd != newMovie?.locCinemaEnd
    )
      return true;
    return false;
  }

  /**
   * Updates tracking after change the tab
   * @param event
   */
  onChangeType(event: ShowtimesTab) {
    this.debugLog('onChangeType', { previousType: this.activeType, nextType: event, movie: this.movie()?.title });
    this.setActiveType(event);
    this.subscribeType = null;
    const movie = this.movie();
    if (!movie) {
      this.updateQueryParams();
      return;
    }

    if (!this.beforeType != null && this.beforeType !== event) {
      this.beforeType = this.activeType;
      this.trackingService.trackEvent({
        action: 'ChangeTab',
        properties: {
          label: `${movie.title} @ ${this.activeType}`,
          category: 'MovieDetail',
          movie: movie.title,
          idMovie: movie.id,
          imdb: movie.imdb,
          tagsMovie: getMovieTags(movie),
          genresMovie: getMovieGenres(movie)
        }
      });
    }
    this.updateQueryParams();
  }

  onSelectButtonOptionClick(event: SelectButtonOptionClickEvent) {
    const nextType = (event.option?.value ??
      (event.index != null ? this.pageTypesOptions[event.index]?.value : undefined)) as ShowtimesTab | undefined;
    this.handleSelectButtonTypeChange(nextType);
  }

  onSelectButtonChange(event: SelectButtonChangeEvent) {
    this.handleSelectButtonTypeChange(event.value as ShowtimesTab | undefined);
  }

  // todo: we could probably move this to afterRender hook in constructor
  updateQueryParams() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const url = new URL(document.location as any);
    if (this.env.appType === 'widget') {
      url.searchParams.set('whereToWatch', this.activeType);
    } else {
      url.searchParams.set('section', this.activeType);
    }

    history.pushState({}, '', url.toString());
  }

  /**
   * @docsNotRequired
   */
  enableTabs() {
    this.pageTypes = [];
    const placeholderAvailable = this.env.mode == 'develop' || this.env?.mode == 'local';
    const placeholderPerformancesGroup = placeholderAvailable
      ? this.env?.placeholderData?.performancesGroup
      : undefined;
    const placeholderStreamingLinks = placeholderAvailable ? this.env?.placeholderData?.streamingLinks : undefined;
    (this.showInCinemas || placeholderPerformancesGroup) && this.pageTypes.push('in-cinemas');
    (this.showWatchAtHome || placeholderStreamingLinks) && this.pageTypes.push('watch-at-home');
    this.showAtFestivals && this.pageTypes.push('at-festivals');
    this.showEnquiries && this.pageTypes.push('enquiries');

    this.handleWidgetsTabsAvailability();
  }

  handleWidgetsTabsAvailability() {
    if (this.widgetEnableTabs.length > 0) {
      // compare widgetEnableTabs to pageTypes and only show the tabs that are available in both
      this.pageTypes = this.pageTypes.filter(type => this.widgetEnableTabs.includes(type));
    }
  }

  onShowtimeTicketingHandle(performanceId) {
    this.router.navigateByUrl(`/get-tickets/${performanceId}`);
  }

  areShowtimesOrStreamingAvailable(): boolean {
    return this.activeType == 'in-cinemas' ? this.showInCinemas : this.showWatchAtHome;
  }

  /**
   * Check if there are invalid entries in {@link pageTypes}.
   *
   * //TODO barbeitoalex propostal - I think this is not necessary.
   */
  activeTypeDoesNotExists() {
    if (this.pageTypes.includes(this.activeType)) {
      return false;
    }
    return true;
  }

  /**
   * Sets {@link usePlaceholderPerformancesGroup} to only work in local enviroment if is available.
   */
  setUsePlaceholderData() {
    if (this.env.mode == 'develop') {
      this.usePlaceholderPerformancesGroup = !!this.env?.placeholderData?.performancesGroup || false;
      if (this.usePlaceholderPerformancesGroup && !this.widgetEnableTabs.includes('in-cinemas'))
        this.widgetEnableTabs.push('in-cinemas');
      this.usePlaceholderStreamingLinks = !!this.env?.placeholderData?.streamingLinks || false;
      if (this.usePlaceholderStreamingLinks && !this.widgetEnableTabs.includes('watch-at-home'))
        this.widgetEnableTabs.push('watch-at-home');
    } else {
      this.usePlaceholderPerformancesGroup = false;
    }
  }

  /**
   * Method that will track the StreamingLoaded event if there are streams present.
   */
  onStreamingPlatformsLoaded() {
    const movie = this.movie();
    this.debugLog('onStreamingPlatformsLoaded callback fired', {
      movie: movie?.title,
      alreadyTracked: this.streamingPlatformsLoadedTracked
    });
    if (!this.streamingPlatformsLoadedTracked && movie) {
      this.streamingPlatformsLoadedTracked = true;
      this.debugLog('Tracking StreamingLoaded');
      this.trackingService.trackEvent({
        action: 'StreamingLoaded',
        properties: {
          category: 'MovieStreaming',
          label: `${movie.title}`,
          movie: movie.title,
          idMovie: movie.id,
          imdb: movie.imdb,
          tagsMovie: getMovieTags(movie),
          genresMovie: getMovieGenres(movie)
        }
      });
    }
  }

  /**
   * Method that will track ShowtimesAvailable action if ther are performances present.
   * Tracking is emitted from in-cinemas source-of-truth component.
   */
  onShowtimesLoaded() {
    this.debugLog('onShowtimesLoaded callback fired', {
      movie: this.movie()?.title,
      alreadyTracked: this.showtimesLoadedTracked
    });
    //
  }

  setActiveType(type: ShowtimesTab | null) {
    this.activeType = type && this.availableTypes.includes(type) ? type : 'in-cinemas';
    this.cd.detectChanges();
  }

  private displayDemandItForm(): void {
    const movie = this.movie();
    this.showDemandItForm = movie ? this.showtimeStreamingService.isDemandItFormEnabled(movie) : false;
  }

  setUpNoOptionsMessage() {
    if (this.isMovieReleased()) {
      this.noOptionsMessage = $localize`:@@public.commons.whereToWatch.noOptionsReleased:No options available yet. Please check back later.`;
      this.noOptionsMessageKey = `public.commons.whereToWatch.noOptionsReleased`;
    }
  }

  isMovieReleased(): boolean {
    const movie = this.movie();
    if (movie) {
      const releaseDate = getDateFromDDMMYYYY(movie.releaseDate);
      const now = new Date();
      return releaseDate <= now;
    } else {
      return false;
    }
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(step, context ?? '');
  }

  private handleSelectButtonTypeChange(nextType: ShowtimesTab | undefined): void {
    if (!nextType || nextType === this.activeType) return;
    this.onChangeType(nextType);
  }
}
